import type { CSSProperties } from 'react';

/** Tema visual do deck CAB (capa CCO + tokens). Persistido em localStorage até existir API. */

export type CabDeckTheme = {
  bg: string;
  blue: string;
  green: string;
  text: string;
  chromeBg: string;
  chromeText: string;
  chromeAccent: string;
  titleFontFamily: string;
  titleFontSizeVw: number;
  titleFontWeight: number;
  dateFontSizeRem: number;
  capaFixedTitle: string;
  useChangeTitleOnCapa: boolean;
};

export const DEFAULT_CAB_DECK_THEME: CabDeckTheme = {
  bg: '#e8e8e8',
  blue: '#25408D',
  green: '#72BD44',
  text: '#25408D',
  chromeBg: '#25408D',
  chromeText: '#FFFFFF',
  chromeAccent: '#72BD44',
  titleFontFamily: '"Segoe UI", Arial, Helvetica, sans-serif',
  titleFontSizeVw: 3.1,
  titleFontWeight: 700,
  dateFontSizeRem: 1,
  capaFixedTitle: '',
  useChangeTitleOnCapa: true,
};

const STORAGE_KEY = 'suportnect.cabDeckTheme.v1';

export function loadCabDeckTheme(): CabDeckTheme {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CAB_DECK_THEME };
    const parsed = JSON.parse(raw) as Partial<CabDeckTheme>;
    return { ...DEFAULT_CAB_DECK_THEME, ...parsed };
  } catch {
    return { ...DEFAULT_CAB_DECK_THEME };
  }
}

export function saveCabDeckTheme(theme: CabDeckTheme): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
}

export function cabDeckThemeToCssVars(theme: CabDeckTheme): CSSProperties {
  return {
    ['--cab-cco-blue' as string]: theme.blue,
    ['--cab-cco-green' as string]: theme.green,
    ['--cab-cco-bg' as string]: theme.bg,
    ['--cab-cco-text' as string]: theme.text,
    ['--cab-cco-chrome-bg' as string]: theme.chromeBg,
    ['--cab-cco-chrome-text' as string]: theme.chromeText,
    ['--cab-cco-chrome-accent' as string]: theme.chromeAccent,
    ['--cab-cco-title-font' as string]: theme.titleFontFamily,
    ['--cab-cco-title-size' as string]: `${theme.titleFontSizeVw}vw`,
    ['--cab-cco-title-weight' as string]: String(theme.titleFontWeight),
    ['--cab-cco-date-size' as string]: `${theme.dateFontSizeRem}rem`,
  };
}

export function resolveCapaTitle(theme: CabDeckTheme, changeHeading: string): string {
  if (!theme.useChangeTitleOnCapa && theme.capaFixedTitle.trim()) {
    return theme.capaFixedTitle.trim();
  }
  return changeHeading;
}

/** Escala de leitura só na apresentação (não altera o deck salvo). */
export const CAB_FONT_SCALE_MIN = 0.8;
export const CAB_FONT_SCALE_MAX = 1.8;
export const CAB_FONT_SCALE_STEP = 0.1;
export const CAB_FONT_SCALE_DEFAULT = 1;

const FONT_SCALE_STORAGE_KEY = 'suportnect.cabPresentFontScale.v1';

export function clampCabFontScale(value: number): number {
  if (!Number.isFinite(value)) return CAB_FONT_SCALE_DEFAULT;
  const stepped = Math.round(value / CAB_FONT_SCALE_STEP) * CAB_FONT_SCALE_STEP;
  return Math.max(CAB_FONT_SCALE_MIN, Math.min(CAB_FONT_SCALE_MAX, Number(stepped.toFixed(1))));
}

export function loadCabFontScale(): number {
  try {
    const raw = window.sessionStorage.getItem(FONT_SCALE_STORAGE_KEY);
    if (!raw) return CAB_FONT_SCALE_DEFAULT;
    return clampCabFontScale(Number(raw));
  } catch {
    return CAB_FONT_SCALE_DEFAULT;
  }
}

export function saveCabFontScale(value: number): void {
  try {
    window.sessionStorage.setItem(FONT_SCALE_STORAGE_KEY, String(clampCabFontScale(value)));
  } catch {
    /* ignore quota / private mode */
  }
}
